import {
  ARTICLE_LIST,
  type ArticleListItem,
} from "@/fixtures/ej-health/medical-news-fixtures";
import {
  CATEGORIES,
  CATEGORY_ARTICLES,
} from "@/fixtures/ej-health/category/fixtures";
import {
  DOCTORS_VIDEOS,
  EXERCISE_VIDEOS,
  HERO_FEATURE,
  HERO_SECONDARY,
  LATEST_NEWS_HERO,
  MEDICAL_HERO,
  FEATURED_HERO,
  WEALTH_HERO,
  COLUMNS_HERO,
  ONE_MIN_HERO,
} from "@/fixtures/ej-health/fixtures";

/* ────────────────────────────────────────────────────────
   Search over EJ Health.

   The masthead had a search box that pushed to the MAIN site's `/search?q=`,
   which searches the parent paper and knows nothing about this vertical — so a
   reader searching 信健康 got HKEJ results, and there was no EJ Health result
   page at all. QA: "missing search result page" and "should have auto
   suggestion when inputting text".

   Modelled on `fixtures/ej-tech/taxonomy.ts`, which answered the same two
   tickets for that vertical.
   ──────────────────────────────────────────────────────── */

export type EjHealthSearchSort = "relevance" | "newest" | "oldest";

export const EJHEALTH_SEARCH_SORTS: ReadonlyArray<{
  value: EjHealthSearchSort;
  label: string;
}> = [
  { value: "relevance", label: "相關度" },
  { value: "newest", label: "最新" },
  { value: "oldest", label: "最舊" },
];

const asItem = (a: {
  title: string;
  excerpt?: string;
  time: string;
  img: string;
  category?: string;
}): ArticleListItem => ({
  title: a.title,
  excerpt: a.excerpt ?? "",
  time: a.time,
  img: a.img,
  category: a.category,
});

/** Everything the site publishes, de-duplicated by title. */
const ALL_ARTICLES: ArticleListItem[] = (() => {
  const pool: ArticleListItem[] = [
    ...ARTICLE_LIST,
    ...CATEGORY_ARTICLES,
    asItem(HERO_FEATURE),
    ...HERO_SECONDARY.map(asItem),
    asItem(LATEST_NEWS_HERO),
    asItem(MEDICAL_HERO),
    asItem(FEATURED_HERO),
    asItem(WEALTH_HERO),
    asItem(COLUMNS_HERO),
    asItem(ONE_MIN_HERO),
    ...[...DOCTORS_VIDEOS, ...EXERCISE_VIDEOS].map((v) => ({
      title: v.title,
      excerpt: "",
      time: v.time,
      img: v.img,
      category: v.category,
      duration: v.duration,
      subscriberOnly: v.subscriberOnly,
    })),
  ];
  const seen = new Set<string>();
  return pool.filter((a) => {
    if (seen.has(a.title)) return false;
    seen.add(a.title);
    return true;
  });
})();

/**
 * Age in minutes, read off the display timestamp.
 *
 * The fixtures carry relative Chinese strings ("15分鐘前", "3小時前") rather than
 * epoch times, so the ordering comes from the same string the row renders.
 * Anything unparseable sorts oldest, which fails predictably. Swap for a real
 * `publishedAt` when the data carries one.
 */
function ageMinutes(time: string): number {
  const n = Number(time.match(/\d+/)?.[0]);
  if (!Number.isFinite(n)) return Number.POSITIVE_INFINITY;
  if (/分鐘/.test(time)) return n;
  if (/小時/.test(time)) return n * 60;
  if (/天/.test(time)) return n * 60 * 24;
  if (/週|星期/.test(time)) return n * 60 * 24 * 7;
  if (/月/.test(time)) return n * 60 * 24 * 30;
  if (/年/.test(time)) return n * 60 * 24 * 365;
  return Number.POSITIVE_INFINITY;
}

/** Title beats excerpt beats category, then the chosen order. */
export function ejHealthSearch(
  query: string,
  sort: EjHealthSearchSort = "relevance",
): ArticleListItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const scored: { article: ArticleListItem; score: number }[] = [];
  for (const a of ALL_ARTICLES) {
    let score = 0;
    if (a.title.toLowerCase().includes(q)) score = 3;
    else if (a.excerpt.toLowerCase().includes(q)) score = 2;
    else if ((a.category ?? "").toLowerCase().includes(q)) score = 1;
    if (score === 0) continue;
    scored.push({ article: a, score });
  }
  if (sort === "newest" || sort === "oldest") {
    const dir = sort === "newest" ? 1 : -1;
    scored.sort(
      (a, b) => dir * (ageMinutes(a.article.time) - ageMinutes(b.article.time)),
    );
  } else {
    scored.sort(
      (a, b) =>
        b.score - a.score ||
        ageMinutes(a.article.time) - ageMinutes(b.article.time),
    );
  }
  return scored.map((s) => s.article);
}

/** The terms offered on the empty search page. */
export const EJHEALTH_POPULAR_SEARCHES = [
  "營養學",
  "糖胖與你",
  "流感疫苗",
  "食物安全",
  "體重管理",
  "中醫",
];

/** Type-ahead: article titles first, then category names, then tags. */
export function ejHealthSuggest(fragment: string, limit = 6): string[] {
  const q = fragment.trim().toLowerCase();
  if (!q) return [];
  const out: string[] = [];
  const push = (v: string) => {
    if (out.length < limit && !out.includes(v)) out.push(v);
  };
  for (const a of ALL_ARTICLES)
    if (a.title.toLowerCase().includes(q)) push(a.title);
  for (const c of Object.values(CATEGORIES))
    if (c.heading.toLowerCase().includes(q)) push(c.heading);
  for (const c of Object.values(CATEGORIES))
    for (const t of c.relatedTags ?? [])
      if (t.toLowerCase().includes(q)) push(t);
  for (const t of EJHEALTH_POPULAR_SEARCHES)
    if (t.toLowerCase().includes(q)) push(t);
  return out;
}

/** The categories offered on the empty search page — label + slug. */
export const EJHEALTH_CATEGORY_LINKS = Object.values(CATEGORIES).map((c) => ({
  label: c.heading,
  slug: c.slug,
}));
